import * as Y from 'yjs'
import { sharedProject, sharedSnapshot, sharedText, writeShared, toBase64, fromBase64 } from './sharedProject.mjs'
import type { LiveRecovery } from './recoveryStore'
import { normalizeProject } from './model'
import { renameReference } from './connectedBible'
import type { StoryProject } from './types'
import type { LiveMessage } from './LiveRoom'

export class LiveDocument {
  doc = new Y.Doc()
  origin = {}
  undo: Y.UndoManager
  generation = 0
  vector = ''
  timer = 0
  composing = false
  deferred: LiveMessage[] = []
  compositionTimer = 0
  recoverySnapshot: Uint8Array = new Uint8Array()
  recoveryUpdates: Uint8Array[] = []
  recoveryTimer = 0
  inputEpoch = 0
  inputChanged = () => { this.inputEpoch++ }
  compositionStart = () => { if ((document.activeElement as HTMLElement | null)?.dataset.livePath) this.composing = true }
  compositionEnd = () => { this.compositionTimer = window.setTimeout(() => { this.composing = false; for (const message of this.deferred.splice(0)) this.receive(message) }, 0) }
  removeListener: () => void
  onUpdate: (project: StoryProject, message?: LiveMessage, local?: boolean) => void = () => {}
  onError: (error: string) => void = () => {}
  constructor(public projectId: string, state: { update: string; vector: string }, public send: (m: unknown) => boolean, listen: (handler: (m: LiveMessage) => void) => () => void, cached?: string | LiveRecovery) {
    Y.applyUpdate(this.doc, fromBase64(state.update))
    this.vector = state.vector
    if (typeof cached === 'string') Y.applyUpdate(this.doc, fromBase64(cached))
    else if (cached) {
      Y.applyUpdate(this.doc, cached.snapshot)
      for (const update of cached.updates) Y.applyUpdate(this.doc, update)
    }
    normalizeProject(sharedProject(this.doc))
    this.recoverySnapshot = Y.encodeStateAsUpdate(this.doc)
    this.undo = new Y.UndoManager(this.doc.getMap('root'), { trackedOrigins: new Set([this.origin]), captureTimeout: 650 })
    this.doc.on('update', (_update, origin) => {
      this.recoveryUpdates = [...this.recoveryUpdates, _update]
      if (!this.recoveryTimer && this.recoveryUpdates.length >= 128) this.recoveryTimer = window.setTimeout(() => {
        this.recoveryTimer = 0
        this.recoverySnapshot = Y.encodeStateAsUpdate(this.doc)
        this.recoveryUpdates = []
      }, 500)
      if (origin !== this.origin && origin !== this.undo) return
      this.generation++
      this.onUpdate(this.project, undefined, true)
      // A short batch limits disk writes, with a maximum latency while typing.
      if (!this.timer) this.timer = window.setTimeout(() => { this.timer = 0; this.transmit() }, 180)
    })
    this.removeListener = listen(message => {
      if (message.type === 'connected') { this.join(); this.transmit() }
      if (message.type === 'error') this.onError(message.error || 'Live edits could not save. Retrying…')
      if (message.type === 'edit' && message.projectId === this.projectId) this.receive(message)
      if ((message.type === 'unavailable' || message.type === 'changed') && message.projectId === this.projectId) this.onError('This story changed or moved to Trash. Your draft has been kept.')
    })
    document.addEventListener('compositionstart', this.compositionStart)
    document.addEventListener('compositionend', this.compositionEnd)
    for (const event of ['input', 'keydown', 'pointerdown']) document.addEventListener(event, this.inputChanged, true)
    this.join()
  }
  get project() { return sharedSnapshot(this.doc) }
  get recovery(): LiveRecovery { return { snapshot: this.recoverySnapshot, updates: this.recoveryUpdates, vector: this.vector } }
  get payload() { return { update: toBase64(Y.encodeStateAsUpdate(this.doc, fromBase64(this.vector))), vector: toBase64(Y.encodeStateVector(this.doc)), generation: this.generation } }
  join() { this.send({ type: 'join', projectId: this.projectId, vector: toBase64(Y.encodeStateVector(this.doc)) }) }
  transmit() { this.send({ type: 'edit', projectId: this.projectId, ...this.payload }) }
  change(project: StoryProject) { writeShared(this.doc, project, this.origin) }
  changeField(path: string[], value: string) {
    const text = sharedText(this.doc, path)
    if (!text) return
    const old = text.toString()
    let start = 0, end = 0
    while (start < old.length && start < value.length && old[start] === value[start]) start++
    while (end < old.length - start && end < value.length - start && old[old.length - end - 1] === value[value.length - end - 1]) end++
    this.doc.transact(() => {
      if (old.length - start - end) text.delete(start, old.length - start - end)
      if (value.length - start - end) text.insert(start, value.slice(start, value.length - end))
    }, this.origin)
  }
  finishRename(path: string[], previous: string) {
    const value = sharedText(this.doc, path)?.toString()
    if (!value || value === previous) return
    const target = path[0] === 'nodes' ? 'scene:' + path[1] : path[1] === 'characters' ? 'character:' + path[2] : 'entry:' + path[2]
    const project = structuredClone(this.project)
    const parent = path.slice(0, -1).reduce<any>((item, key) => Array.isArray(item) ? item.find(value => value.id === key) : item?.[key], project)
    if (!parent) return
    // Resolve old mentions against a temporary name, then commit only the final
    // rename. Intermediate prefixes must never capture another entry's links.
    parent[path.at(-1)!] = previous
    this.change(renameReference(project, target, value))
  }
  receive(message: LiveMessage) {
    if (!message.update || !message.vector) return
    if (this.composing) { this.deferred.push(message); return }
    const update = fromBase64(message.update)
    // Ordinary save receipts contain no document change. Do not rebuild the
    // project or restore an old caret position after the writer types again.
    if (update.length === 2 && update[0] === 0 && update[1] === 0) {
      this.vector = message.vector
      this.onUpdate(this.project, message)
      return
    }
    // Structural edits can conflict (for example, both writers deleting the
    // last two different scenes). Keep the local recovery copy usable if their
    // combined structure is invalid instead of crashing the controlled editor.
    const candidate = new Y.Doc()
    try {
      Y.applyUpdate(candidate, Y.encodeStateAsUpdate(this.doc))
      Y.applyUpdate(candidate, update)
      normalizeProject(sharedProject(candidate))
    } catch {
      this.onError('Concurrent structural changes need recovery. Your browser draft is safe. Download it, then recover it as a new project or reopen the server story.')
      return
    } finally { candidate.destroy() }
    const before = this.project
    // Relative positions preserve the local selection when remote text arrives.
    const element = document.activeElement as HTMLTextAreaElement | HTMLInputElement | null
    const path = element?.dataset.livePath
    const text = path ? sharedText(this.doc, JSON.parse(path)) : null
    const beforeText = text?.toString(), inputEpoch = this.inputEpoch
    const selection = text && element?.selectionStart != null ? [Y.createRelativePositionFromTypeIndex(text, element.selectionStart), Y.createRelativePositionFromTypeIndex(text, element.selectionEnd!)] : null
    Y.applyUpdate(this.doc, update, 'remote')
    this.vector = message.vector
    const next = this.project
    const valueAt = (project: StoryProject, key: string) => JSON.parse(key).reduce((value: any, part: string) => Array.isArray(value) ? value.find(item => item.id === part) : value?.[part], project)
    const fields = [...document.querySelectorAll<HTMLElement>('[data-live-path]')].map(element => element.dataset.livePath!).filter(key => valueAt(before, key) !== valueAt(next, key))
    this.onUpdate(next, { ...message, fields })
    if (selection && element && beforeText !== text?.toString()) requestAnimationFrame(() => {
      if (document.activeElement !== element || this.inputEpoch !== inputEpoch) return
      const [start, end] = selection.map(position => Y.createAbsolutePositionFromRelativePosition(position, this.doc)?.index)
      if (start != null && end != null) element.setSelectionRange(start, end)
    })
  }
  destroy() { window.clearTimeout(this.timer); window.clearTimeout(this.compositionTimer); window.clearTimeout(this.recoveryTimer); document.removeEventListener('compositionstart', this.compositionStart); document.removeEventListener('compositionend', this.compositionEnd); for (const event of ['input', 'keydown', 'pointerdown']) document.removeEventListener(event, this.inputChanged, true); this.removeListener(); this.undo.destroy(); this.doc.destroy() }
}
